import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook to debounce a function call. From ChatGPT.
 * @param fn Function to debounce.
 * @param delay Delay in ms.
 * @returns Debounced function.
 */
export function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number = 500): (...args: Parameters<T>) => void {
  const fnRef = useRef(fn);
  const timer = useRef<NodeJS.Timeout | null>(null);

  // Always keep the latest fn reference
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  return useCallback((...args: Parameters<T>) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      fnRef.current(...args);
    }, delay);
  }, [delay]);
}
